// src/app/redux/sagas/noteDeFraisSaga.js
import { call, put } from "redux-saga/effects";
import Swal from "sweetalert2";
import { API_BASE_URL, getRequest, postRequest, putRequest, deleteRequest } from "../../../api";

import {
    setNoteDeFraisLoading,
    fetchNoteDeFraisSuccess,
    fetchNoteDeFraisFail,
    fetchNoteDeFraisByIdSuccess,
    fetchNoteDeFraisByIdFail,
    createNoteDeFraisSuccess,
    createNoteDeFraisFail,
    updateNoteDeFraisSuccess,
    updateNoteDeFraisFail,
    deleteNoteDeFraisSuccess,
    deleteNoteDeFraisFail,
    fetchNoteDeFraisActifsSuccess,
    fetchNoteDeFraisActifsFail,
} from "../actions/noteDeFraisActions";

const extractErrorMessage = (error, fallback) => {
    let msg = fallback;

    if (error?.response?.data?.message) msg = error.response.data.message;
    else if (error?.response?.data?.error) msg = error.response.data.error;
    else if (error?.message) msg = error.message;

    return msg;
};

// ===================== FETCH ALL =====================
export function* fetchNoteDeFraisSaga() {
    const token = sessionStorage.getItem('token');
    const url = `${API_BASE_URL}/api/notedefrais`;

    yield put(setNoteDeFraisLoading(true));

    try {
        console.group("🔵 FETCH_NOTE_DE_FRAIS_REQUEST");
        console.log("🌐 URL:", url);
        console.groupEnd();

        const resp = yield call(getRequest, { url, token });

        console.group("✅ FETCH_NOTE_DE_FRAIS_SUCCESS");
        console.log("📥 Notes de frais:", resp.data.length);
        console.groupEnd();

        yield put(fetchNoteDeFraisSuccess(resp.data || []));
        yield put(setNoteDeFraisLoading(false));
    } catch (error) {
        console.group("❌ FETCH_NOTE_DE_FRAIS_FAIL");
        console.error("🚨 Erreur:", error.message);
        console.groupEnd();

        yield put(fetchNoteDeFraisFail(error.message));
        yield put(setNoteDeFraisLoading(false));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "",
            text: "Erreur chargement notes de frais",
            timer: 2000,
            showConfirmButton: false,
        });
    }
}

// ===================== FETCH BY ID =====================
export function* fetchNoteDeFraisByIdSaga(action) {
    const token = sessionStorage.getItem('token');
    const id = action.payload;
    const url = `${API_BASE_URL}/api/notedefrais/${id}`;

    yield put(setNoteDeFraisLoading(true));

    try {
        console.group("🔵 FETCH_NOTE_DE_FRAIS_BY_ID_REQUEST");
        console.log("🆔 ID:", id);
        console.groupEnd();

        const resp = yield call(getRequest, { url, token });

        console.group("✅ FETCH_NOTE_DE_FRAIS_BY_ID_SUCCESS");
        console.log("📥 Note de frais:", resp.data);
        console.groupEnd();

        yield put(fetchNoteDeFraisByIdSuccess(resp.data));
        yield put(setNoteDeFraisLoading(false));
    } catch (error) {
        console.group("❌ FETCH_NOTE_DE_FRAIS_BY_ID_FAIL");
        console.error("🚨 Erreur:", error.message);
        console.groupEnd();

        yield put(fetchNoteDeFraisByIdFail(error.message));
        yield put(setNoteDeFraisLoading(false));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "",
            text: "Note de frais introuvable",
            timer: 2000,
            showConfirmButton: false,
        });
    }
}

// ===================== CREATE =====================
export function* createNoteDeFraisSaga(action) {
    const token = sessionStorage.getItem('token');
    const url = `${API_BASE_URL}/api/notedefrais`;
    const data = action.payload;

    yield put(setNoteDeFraisLoading(true));

    try {
        console.group("🔵 CREATE_NOTE_DE_FRAIS_REQUEST");
        console.log("📊 Données:", data);
        console.groupEnd();

        const resp = yield call(postRequest, { url, data, token });

        console.group("✅ CREATE_NOTE_DE_FRAIS_SUCCESS");
        console.log("📥 Note de frais créée:", resp.data);
        console.groupEnd();

        yield put(createNoteDeFraisSuccess(resp.data));
        yield put(setNoteDeFraisLoading(false));

        yield call([Swal, Swal.fire], {
            icon: "success",
            title: "",
            text: "Note de frais ajoutée",
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        console.group("❌ CREATE_NOTE_DE_FRAIS_FAIL");
        console.error("🚨 Erreur:", error.message);
        console.groupEnd();

        const errorMessage = extractErrorMessage(error, "Erreur lors de l'ajout");
        yield put(createNoteDeFraisFail(errorMessage));
        yield put(setNoteDeFraisLoading(false));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "Erreur",
            text: errorMessage,
            confirmButtonText: "OK",
            confirmButtonColor: "#d33",
        });
    }
}

// ===================== UPDATE =====================
export function* updateNoteDeFraisSaga(action) {
    const token = sessionStorage.getItem("token");
    const { id, payload } = action.payload;
    const data = action.payload.payload;

    const url = `${API_BASE_URL}/api/notedefrais/${id}`;
    yield put(setNoteDeFraisLoading(true));

    try {
        console.group("🔵 UPDATE_NOTE_DE_FRAIS_REQUEST");
        console.log("🆔 ID:", id);
        console.log("📦 Payload:", payload);
        console.groupEnd();

        const resp = yield call(putRequest, { url, data, token });

        console.group("✅ UPDATE_NOTE_DE_FRAIS_SUCCESS");
        console.log("✅ Updated:", resp.data);
        console.groupEnd();

        yield put(updateNoteDeFraisSuccess(resp.data));
        yield put(setNoteDeFraisLoading(false));

        yield call([Swal, Swal.fire], {
            icon: "success",
            title: "Succès",
            text: "Note de frais mise à jour",
            timer: 1400,
            showConfirmButton: false,
        });
    } catch (error) {
        console.group("❌ UPDATE_NOTE_DE_FRAIS_FAIL");
        console.error("🚨 Erreur complète:", error);
        console.error("🚨 Response data:", error.response?.data);
        console.error("🚨 Response status:", error.response?.status);
        console.groupEnd();

        const errorMessage = extractErrorMessage(error, "Erreur lors de la mise à jour");
        yield put(updateNoteDeFraisFail(errorMessage));
        yield put(setNoteDeFraisLoading(false));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "Erreur",
            text: errorMessage,
            confirmButtonText: "OK",
            confirmButtonColor: "#d33",
        });
    }
}

// ===================== DELETE =====================
export function* deleteNoteDeFraisSaga(action) {
    const token = sessionStorage.getItem('token');
    const id = action.payload;
    const url = `${API_BASE_URL}/api/notedefrais/${id}`;

    yield put(setNoteDeFraisLoading(true));

    try {
        console.group("🔵 DELETE_NOTE_DE_FRAIS_REQUEST");
        console.log("🆔 ID:", id);
        console.groupEnd();

        yield call(deleteRequest, { url, token });

        console.group("✅ DELETE_NOTE_DE_FRAIS_SUCCESS");
        console.log("🗑️ Supprimé:", id);
        console.groupEnd();

        yield put(deleteNoteDeFraisSuccess(id));
        yield put(setNoteDeFraisLoading(false));

        yield call([Swal, Swal.fire], {
            icon: "success",
            title: "",
            text: "Note de frais supprimée",
            timer: 1400,
            showConfirmButton: false,
        });
    } catch (error) {
        console.group("❌ DELETE_NOTE_DE_FRAIS_FAIL");
        console.error("🚨 Erreur complète:", error);
        console.error("🚨 Response data:", error.response?.data);
        console.error("🚨 Response status:", error.response?.status);
        console.groupEnd();

        yield put(setNoteDeFraisLoading(false));

        let errorMessage = "Erreur lors de la suppression";

        if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
        } else if (error.response?.data?.error) {
            errorMessage = error.response.data.error;
        } else if (error.message) {
            errorMessage = error.message;
        }

        yield put(deleteNoteDeFraisFail(errorMessage));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "Erreur de suppression",
            text: errorMessage,
            confirmButtonText: "OK",
            confirmButtonColor: "#d33",
        });
    }
}

// ===================== FETCH ACTIFS =====================
export function* fetchNoteDeFraisActifsSaga() {
    const token = sessionStorage.getItem('token');
    const url = `${API_BASE_URL}/api/notedefrais/actifs`;

    yield put(setNoteDeFraisLoading(true));

    try {
        console.group("🔵 FETCH_NOTE_DE_FRAIS_ACTIFS_REQUEST");
        console.log("🌐 URL:", url);
        console.groupEnd();

        const resp = yield call(getRequest, { url, token });

        console.group("✅ FETCH_NOTE_DE_FRAIS_ACTIFS_SUCCESS");
        console.log("📥 Notes de frais actives:", resp.data.length);
        console.groupEnd();

        yield put(fetchNoteDeFraisActifsSuccess(resp.data || []));
        yield put(setNoteDeFraisLoading(false));
    } catch (error) {
        console.group("❌ FETCH_NOTE_DE_FRAIS_ACTIFS_FAIL");
        console.error("🚨 Erreur:", error.message);
        console.groupEnd();

        yield put(fetchNoteDeFraisActifsFail(error.message));
        yield put(setNoteDeFraisLoading(false));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "",
            text: "Erreur chargement notes actives",
            timer: 2000,
            showConfirmButton: false,
        });
    }
}